import React, { ReactNode, useMemo } from "react";
import { POSAMode } from "../../POSAEditor/model";
import POSAPreviewCard from "../POSAPreviewCard";
import { useDispatch } from "react-redux";
import {
  useAddingPOSAToBasketIdsSelector,
  usePOSATypesSelector
} from "../../../../../redux/actions/wheel";
import { useAccountToDisplaySelector } from "../../../../../redux/actions/user";
import POSAPlaceholder from "../../../../../components/SVGPreview/POSAPlaceholder";
import {
  POSA_FORMAT,
  POSATypeJavascriptDTO
} from "@tootsweet/model/lottery/POSAType";
import { useCustomPOSASelector } from "../../../../../redux/customPOSA";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import Carousel from "../../../../../components/Carousel/Carousel";
import { addPOSAToBasket } from "../../../../../redux/saas/thunks";
import { tt } from "../../../../../utils/i18n/i18n";
import NiceModal from "@ebay/nice-modal-react";
import PostPaymentModal from "../../../../../components/modal/ConfirmPayment/PostPaymentModal";

interface Props {
  format: POSA_FORMAT;
  imgURL: string;
  hidePlaceholderTitle?: boolean;
  posaList?: POSATypeJavascriptDTO[];
}

export default function POSACarousel(props: Props) {
  const dispatch = useDispatch();

  const { accountToDisplay } = useAccountToDisplaySelector();

  const posaTypes = usePOSATypesSelector();
  const customPOSA = useCustomPOSASelector();
  const addingPOSAToBasketIds = useAddingPOSAToBasketIdsSelector();

  const customPOSAForFormat = useMemo(() => {
    return customPOSA?.filter((c) => c.format === props.format) || [];
  }, [props.format, customPOSA]);

  const nonCustomPOSA = useMemo(() => {
    const posaFilterByFormat = posaTypes.filter(
      (type) => type.format === props.format
    );
    return posaFilterByFormat.filter((type) => {
      return !type.isCustom;
    });
  }, [props.format, posaTypes, accountToDisplay]);

  const canOrderPOSA = useMemo(() => {
    return (
      accountToDisplay?.businessUser?.shippingAddress?.countryCode?.toLowerCase() ===
      "fr"
    );
  }, [accountToDisplay]);

  const SLIDES = useMemo(() => {
    const res: ReactNode[] = [];

    res.push(
      <POSAPlaceholder
        posaMode={POSAMode.Offline}
        format={props.format}
        imgURL={props.imgURL}
        cover={true}
        hideTitle={props.hidePlaceholderTitle}
        posaList={props.posaList}
      />
    );

    let slides = customPOSAForFormat
      .map((customPOSA) => {
        const posaType = posaTypes.find((p) => p._id === customPOSA.posaTypeId);

        if (!posaType) {
          return null;
        }

        const isLoading = addingPOSAToBasketIds.includes(posaType._id);

        return (
          <React.Fragment key={customPOSA._id}>
            <POSAPreviewCard
              posaMode={POSAMode.Offline}
              posa={posaType}
              eventId="no-custom"
              isLoading={isLoading}
              handleSelected={() => {
                if (isLoading) {
                  return;
                }
                if (!canOrderPOSA) {
                  void NiceModal.show(PostPaymentModal, {
                    headerText: tt("cannot_order_posa_outside_of_france_title"),
                    bodyText: tt("cannot_order_posa_outside_of_france_text"),
                    buttonText: tt("ok")
                  });
                  return;
                }
                dispatch(addPOSAToBasket(posaType.name, posaType._id));
              }}
              customPOSA={customPOSA}
            />
          </React.Fragment>
        );
      })
      .filter((item) => !!item);
    slides.forEach((item) => res.push(item));

    return res;
  }, [
    nonCustomPOSA,
    customPOSAForFormat,
    canOrderPOSA,
    addingPOSAToBasketIds
  ]);

  return <Carousel slides={SLIDES} baseId={props.format || "posa-carousel"} />;
}
